







































































































































import { computed, ComputedRef, defineComponent, ref, SetupContext } from '@vue/composition-api';
import { area as turfArea } from '@turf/turf';
import AddStratificationDialog from '@/components/dialog/AddStratificationDialog.vue';
import CollectMap from '@/components/map/CollectMap.vue';
import { useAllResources } from '@/store/modules/resources';
import { useAllSamplingCollections } from '@/store/modules/samplingCollections';
import { LocationCollection, SamplingCollection } from '@/store/types';
import ConfirmDialog from '../../components/dialog/ConfirmDialog.vue';
import { SQUARE_METERS_PER_ACRE, SQUARE_METERS_PER_HECTARE } from '../../constants';
import soilApiService from '../../services/soilApiService';
import { generateDemoStratification } from '../../utils/demoStratification';
import ViewStratificationDetailsButton from '../stratify/ViewStratificationDetailsButton.vue';
import AreaEditButton from './Area/AreaEditButton.vue';
import CreateSamplingCollectionButton from './CreateSamplingCollectionButton/CreateSamplingCollectionButton.vue';
import FieldAddress from './FieldAddress/FieldAddress.vue';
import FieldContact from './FieldContact/FieldContact.vue';
import FieldEditButton from './FieldEdit/FieldEditButton.vue';
import StratificationDeleteButton from './StratificationDeleteButton.vue';

export default defineComponent({
	components: {
		CollectMap,
		FieldAddress,
		FieldContact,
		CreateSamplingCollectionButton,
		FieldEditButton,
		AreaEditButton,
		AddStratificationDialog,
		StratificationDeleteButton,
		ConfirmDialog,
		ViewStratificationDetailsButton,
	},
	props: {
		id: { type: String, required: true },
	},
	setup(props, { root }: SetupContext) {
		const { isLoading, isLoaded, hasError } = useAllResources(root.$store);
		const { isLoaded: samplingCollectionsAreLoaded } = useAllSamplingCollections(root.$store);
		const field = computed(() => root.$store.getters['resources/getFieldById'](props.id));
		const stratifications = computed(() => root.$store.getters['resources/getStratificationsByFieldId'](props.id));
		const area = computed(() => root.$store.getters['resources/getAreasByFieldId'](props.id)?.[0]);
		const locationCollections: ComputedRef<LocationCollection[]> = computed(() =>
			root.$store.getters['resources/getLocationCollectionsByFieldId'](props.id)
		);
		const locations = computed(() => root.$store.getters['resources/getLocationsByFieldId'](props.id));
		const draftSamplingCollections = computed(() => {
			const locationCollectionIds = locationCollections.value.map(({ id }) => id);
			const draftSamplingCollections: SamplingCollection[] = root.$store.getters['samplingCollections/getByFieldId'](
				props.id
			);
			const draftsWithStratifications = draftSamplingCollections.filter((sc) =>
				locationCollectionIds.includes(sc.object)
			);
			return draftsWithStratifications;
		});
		const submittedSamplingCollections = computed(() =>
			root.$store.getters['resources/getSamplingCollectionsByFieldId'](props.id)
		);
		const samplingCollections = computed(() => [
			...submittedSamplingCollections.value,
			...draftSamplingCollections.value,
		]);
		const showStratifyDialog = ref(false);
		const showDemoDialog = ref(false);
		const demoPointsIsLoading = ref(false);
		const demoPointsError = ref<string | null>(null);
		const isMenuOpen = ref(false);

		const getSamplingProgress = (samplingCollection: SamplingCollection) =>
			`${samplingCollection.members.length} / ${locationCollections.value[0].features.length} locations collected`;

		const createDemoPoints = async () => {
			demoPointsIsLoading.value = true;
			demoPointsError.value = null;
			try {
				const strat = generateDemoStratification(field.value, area.value);
				await soilApiService.createStratification(strat, area.value.id);
				demoPointsIsLoading.value = false;
				showDemoDialog.value = false;
				root.$store.dispatch('resources/getAllResources');
			} catch (error) {
				demoPointsError.value = 'There was an error creating demo points. Please try again.';
			} finally {
				demoPointsIsLoading.value = false;
			}
		};

		const areaByUnit = computed(() => {
			const squareMeters = area.value ? turfArea(area.value) : 0;
			const acres = (squareMeters / SQUARE_METERS_PER_ACRE).toFixed(2);
			const hectares = (squareMeters / SQUARE_METERS_PER_HECTARE).toFixed(2);
			return { acres, hectares };
		});

		return {
			createDemoPoints,
			isLoading,
			isLoaded,
			field,
			area,
			stratifications,
			locationCollections,
			locations,
			hasError,
			draftSamplingCollections,
			samplingCollections,
			getSamplingProgress,
			areaByUnit,
			showStratifyDialog,
			showDemoDialog,
			demoPointsError,
			demoPointsIsLoading,
			isMenuOpen,
			samplingCollectionsAreLoaded,
		};
	},
});
